import { useState, useEffect, useRef, useCallback } from "react";

// ─── Embedded C Interpreter ───────────────────────────────────────────────────
function parseAndRunEmbeddedC(code, ctx) {
    // Simple interpreter that maps common embedded C patterns to actions
    const actions = [];
    const lines = code.split("\n").map(l => l.trim()).filter(Boolean);

    const motorSpeeds = [0, 0, 0];
    const leds = [false, false, false, false, false, false];
    let lcdText = "";
    let buzzerOn = false;
    let buzzerFreq = 440;
    let delays = [];
    let threads = [];
    let priority = [1, 1, 1];
    let interrupts = [];
    let scheduleMode = "FIFO";

    lines.forEach((line, i) => {
        // Motor speed: setMotorSpeed(n, speed)
        let m = line.match(/setMotorSpeed\s*\(\s*(\d)\s*,\s*(\d+)\s*\)/);
        if (m) { const idx = parseInt(m[1])-1; motorSpeeds[idx] = Math.min(100, parseInt(m[2])); }

        // LED: setLED(n, ON/OFF)
        m = line.match(/setLED\s*\(\s*(\d)\s*,\s*(ON|OFF|1|0)\s*\)/i);
        if (m) { leds[parseInt(m[1])-1] = m[2]==="ON"||m[2]==="1"; }

        // LCD: lcdPrint("text")
        m = line.match(/lcdPrint\s*\(\s*"([^"]*)"\s*\)/);
        if (m) lcdText = m[1];

        // LCD: lcdPrint(var) or lcdPrint(expression)
        m = line.match(/lcdPrint\s*\(\s*([^")\s][^)]*)\s*\)/);
        if (m && !lcdText) lcdText = `[${m[1]}]`;

        // Buzzer: setBuzzer(ON/OFF, freq?)
        m = line.match(/setBuzzer\s*\(\s*(ON|OFF|1|0)\s*(?:,\s*(\d+))?\s*\)/i);
        if (m) { buzzerOn = m[1]==="ON"||m[1]==="1"; if(m[2]) buzzerFreq=parseInt(m[2]); }

        // Alarm: triggerAlarm()
        if (line.match(/triggerAlarm\s*\(\)/)) buzzerOn = true;

        // delay_ms
        m = line.match(/delay_ms\s*\(\s*(\d+)\s*\)/);
        if (m) delays.push(parseInt(m[1]));

        // Thread: createThread(name, priority)
        m = line.match(/createThread\s*\(\s*"([^"]*)"\s*,\s*(\d+)\s*\)/);
        if (m) threads.push({ name: m[1], prio: parseInt(m[2]) });

        // setPriority(motor, prio)
        m = line.match(/setPriority\s*\(\s*(\d)\s*,\s*(\d+)\s*\)/);
        if (m) priority[parseInt(m[1])-1] = parseInt(m[2]);

        // attachInterrupt(pin, handler)
        m = line.match(/attachInterrupt\s*\(\s*(\w+)\s*,\s*(\w+)\s*\)/);
        if (m) interrupts.push({ pin: m[1], handler: m[2] });

        // Scheduler
        if (line.match(/scheduler\s*=\s*RR/i)) scheduleMode = "Round Robin";
        if (line.match(/scheduler\s*=\s*FIFO/i)) scheduleMode = "FIFO";
        if (line.match(/scheduler\s*=\s*PRIORITY/i)) scheduleMode = "Priority";
        if (line.match(/RTOS_SCHEDULE\s*\(\s*PRIORITY\s*\)/i)) scheduleMode = "Priority";
        if (line.match(/RTOS_SCHEDULE\s*\(\s*RR\s*\)/i)) scheduleMode = "Round Robin";

        // stopMotor
        m = line.match(/stopMotor\s*\(\s*(\d)\s*\)/);
        if (m) motorSpeeds[parseInt(m[1])-1] = 0;

        // stopAll
        if (line.match(/stopAll\s*\(\)/)) { motorSpeeds[0]=motorSpeeds[1]=motorSpeeds[2]=0; }
    });

    // Auto-detect acceleration from comments or code patterns
    let accel = [false, false, false];
    if (code.match(/accelerate|ramp|PWM|pwm/i)) accel = accel.map(() => true);

    return {
        motorSpeeds,
        leds,
        lcdText: lcdText || (code.length > 10 ? "Program Running..." : ""),
        buzzerOn,
        buzzerFreq,
        totalDelay: delays.reduce((a,b)=>a+b,0),
        threads,
        interrupts,
        priority,
        scheduleMode,
        accel,
        linesExecuted: lines.length,
    };
}

// ─── Motor 3D Component ────────────────────────────────────────────────────────
function Motor3D({ index, speed, priority, accel, interruptActive }) {
    const rpm = Math.round(speed * 30);
    const color = speed === 0 ? "#1a1a2e" : speed < 40 ? "#0d7377" : speed < 70 ? "#14a085" : "#00ff88";
    const glow = speed > 0 ? `0 0 ${speed/2}px ${color}, 0 0 ${speed}px ${color}40` : "none";
    const rotDuration = speed > 0 ? `${Math.max(0.2, 2 - speed/60)}s` : "0s";

    return (
        <div style={{ textAlign: "center", position: "relative" }}>
            <div style={{
                background: "linear-gradient(145deg, #0a0a1a, #1a1a3e)",
                border: `2px solid ${speed > 0 ? color : "#333"}`,
                borderRadius: 12, padding: "12px 10px", position: "relative",
                boxShadow: speed > 0 ? glow : "none",
                transition: "all 0.4s ease",
            }}>
                {/* Motor body */}
                <div style={{ position: "relative", width: 80, height: 80, margin: "0 auto" }}>
                    {/* Stator rings */}
                    {[0,1,2].map(r => (
                        <div key={r} style={{
                            position: "absolute", inset: r*8, borderRadius: "50%",
                            border: `2px solid ${r===0?"#333":r===1?"#444":"#555"}`,
                            background: r===2 ? `radial-gradient(circle, #0d0d2a, #1a1a3e)` : "transparent",
                        }}/>
                    ))}
                    {/* Rotor */}
                    <div style={{
                        position: "absolute", inset: 24, borderRadius: "50%",
                        background: `conic-gradient(${color} 0deg, #0a0a1a 90deg, ${color} 180deg, #0a0a1a 270deg, ${color} 360deg)`,
                        animation: speed > 0 ? `spin ${rotDuration} linear infinite` : "none",
                        boxShadow: speed > 0 ? `0 0 12px ${color}` : "none",
                    }}/>
                    {/* Center hub */}
                    <div style={{
                        position: "absolute", inset: 32, borderRadius: "50%",
                        background: "#0a0a1a", border: `1px solid ${color}44`,
                    }}/>
                    {/* Speed indicator lines */}
                    {[0,60,120,180,240,300].map(a => (
                        <div key={a} style={{
                            position: "absolute", top: "50%", left: "50%",
                            width: 2, height: 18, transformOrigin: "50% 100%",
                            transform: `rotate(${a}deg) translateX(-50%)`,
                            background: speed > 0 ? `${color}88` : "#33333388",
                            borderRadius: 1,
                        }}/>
                    ))}
                </div>

                {/* Shaft */}
                <div style={{
                    width: 40, height: 8, margin: "4px auto 0",
                    background: `linear-gradient(90deg, #333, ${speed>0?color:"#555"}, #333)`,
                    borderRadius: 4, boxShadow: speed>0 ? `0 0 8px ${color}` : "none",
                }}/>

                {/* Stats */}
                <div style={{ marginTop: 8, fontFamily: "'Courier New', monospace" }}>
                    <div style={{ color: color, fontSize: 18, fontWeight: "bold" }}>{speed}%</div>
                    <div style={{ color: "#888", fontSize: 10 }}>{rpm} RPM</div>
                    {accel && speed > 0 && <div style={{ color: "#ffaa00", fontSize: 9 }}>⚡ ACCEL</div>}
                    {interruptActive && <div style={{ color: "#ff4444", fontSize: 9 }}>⚠ IRQ</div>}
                </div>

                <div style={{ color: "#666", fontSize: 10, marginTop: 4 }}>MOTOR {index+1}</div>
                <div style={{
                    position: "absolute", top: 4, right: 6, fontSize: 9,
                    color: "#888", fontFamily: "monospace"
                }}>P{priority}</div>
            </div>

            {/* Base plate */}
            <div style={{
                width: 90, height: 8, margin: "2px auto 0",
                background: "linear-gradient(90deg, #111, #222, #111)",
                borderRadius: "0 0 6px 6px", border: "1px solid #333",
            }}/>
        </div>
    );
}

// ─── LCD Display ──────────────────────────────────────────────────────────────
function LCDDisplay({ text, accelData }) {
    const [cursor, setCursor] = useState(true);
    useEffect(() => {
        const t = setInterval(() => setCursor(c => !c), 530);
        return () => clearInterval(t);
    }, []);

    const displayText = text || "READY...";
    const line1 = displayText.slice(0, 16).padEnd(16, " ");
    const line2 = (accelData ? `ACC: ${accelData}` : displayText.slice(16, 32)).padEnd(16, " ").slice(0,16);

    return (
        <div style={{
            background: "#0a1a0a",
            border: "3px solid #1a3a1a",
            borderRadius: 8, padding: "12px 16px",
            boxShadow: "inset 0 2px 8px #000, 0 0 20px #00ff4422",
            position: "relative", overflow: "hidden",
        }}>
            {/* Scan lines */}
            <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                background: "repeating-linear-gradient(0deg, transparent, transparent 2px, #00000022 2px, #00000022 4px)",
            }}/>
            {/* Glare */}
            <div style={{
                position: "absolute", top: 0, left: 0, right: 0, height: "40%",
                background: "linear-gradient(180deg, #ffffff08, transparent)", pointerEvents: "none",
            }}/>

            <div style={{ fontFamily: "'Courier New', monospace", position: "relative" }}>
                <div style={{ color: "#33ff33", fontSize: 13, letterSpacing: 2, marginBottom: 4,
                    textShadow: "0 0 8px #00ff44" }}>
                    {line1}{cursor ? "▊" : " "}
                </div>
                <div style={{ color: "#22cc22", fontSize: 13, letterSpacing: 2,
                    textShadow: "0 0 6px #00ff33" }}>
                    {line2}
                </div>
            </div>
            <div style={{ color: "#115511", fontSize: 9, marginTop: 6, fontFamily: "monospace" }}>
                LCD 16×2 │ I²C:0x27
            </div>
        </div>
    );
}

// ─── Buzzer / Alarm ───────────────────────────────────────────────────────────
function BuzzerAlarm({ active, freq }) {
    const [pulse, setPulse] = useState(0);
    const audioRef = useRef(null);
    const oscRef = useRef(null);
    const gainRef = useRef(null);

    useEffect(() => {
        if (active) {
            try {
                if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
                const ctx = audioRef.current;
                oscRef.current = ctx.createOscillator();
                gainRef.current = ctx.createGain();
                oscRef.current.connect(gainRef.current);
                gainRef.current.connect(ctx.destination);
                oscRef.current.type = "square";
                oscRef.current.frequency.setValueAtTime(freq || 880, ctx.currentTime);
                gainRef.current.gain.setValueAtTime(0.05, ctx.currentTime);
                oscRef.current.start();
            } catch(e) {}
            const t = setInterval(() => setPulse(p => (p+1)%3), 300);
            return () => { clearInterval(t); try { oscRef.current?.stop(); } catch(e) {} };
        } else {
            try { oscRef.current?.stop(); } catch(e) {}
        }
    }, [active, freq]);

    return (
        <div style={{
            background: active ? "radial-gradient(circle, #3a0000, #1a0000)" : "linear-gradient(145deg, #1a1a1a, #0a0a0a)",
            border: `2px solid ${active ? "#ff0000" : "#333"}`,
            borderRadius: 12, padding: 16, textAlign: "center",
            boxShadow: active ? `0 0 30px #ff000066, 0 0 60px #ff000033` : "none",
            transition: "all 0.2s",
        }}>
            {/* Buzzer dome */}
            <div style={{
                width: 70, height: 70, borderRadius: "50%", margin: "0 auto 8px",
                background: active
                    ? `radial-gradient(circle at 35% 35%, #ff6644, #cc0000, #880000)`
                    : `radial-gradient(circle at 35% 35%, #444, #222, #111)`,
                border: `3px solid ${active ? "#ff4422" : "#444"}`,
                boxShadow: active ? `0 0 ${10 + pulse*10}px #ff3300` : "inset 0 2px 4px #000",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.1s",
                position: "relative", overflow: "hidden",
            }}>
                {/* Grill lines */}
                {[...Array(6)].map((_, i) => (
                    <div key={i} style={{
                        position: "absolute", left: 10, right: 10, height: 1,
                        background: active ? "#ff664433" : "#33333388",
                        top: 10 + i * 8,
                    }}/>
                ))}
                <div style={{ fontSize: 24, zIndex: 1 }}>{active ? "🔔" : "🔕"}</div>
            </div>

            {active && (
                <div style={{ color: "#ff4444", fontFamily: "monospace", fontSize: 11, fontWeight: "bold",
                    animation: "blink 0.3s infinite" }}>
                    ⚠ ALARM {freq}Hz ⚠
                </div>
            )}
            {!active && <div style={{ color: "#555", fontSize: 11, fontFamily: "monospace" }}>BUZZER IDLE</div>}
            <div style={{ color: "#444", fontSize: 9, marginTop: 4, fontFamily: "monospace" }}>GPIO PIN 9</div>
        </div>
    );
}

// ─── LED Panel ────────────────────────────────────────────────────────────────
function LEDPanel({ leds }) {
    const colors = ["#ff0000","#ff8800","#ffff00","#00ff00","#00aaff","#aa00ff"];
    const names = ["D1","D2","D3","D4","D5","D6"];
    return (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            {leds.slice(0,6).map((on, i) => (
                <div key={i} style={{ textAlign: "center" }}>
                    <div style={{
                        width: 22, height: 22, borderRadius: "50%",
                        background: on ? colors[i] : "#1a1a1a",
                        border: `2px solid ${on ? colors[i] : "#333"}`,
                        boxShadow: on ? `0 0 12px ${colors[i]}, 0 0 24px ${colors[i]}66` : "none",
                        margin: "0 auto 2px",
                        transition: "all 0.2s",
                    }}/>
                    <div style={{ color: "#555", fontSize: 8, fontFamily: "monospace" }}>{names[i]}</div>
                </div>
            ))}
        </div>
    );
}

// ─── Thread Visualizer ────────────────────────────────────────────────────────
function ThreadPanel({ threads, scheduleMode }) {
    if (!threads.length) return (
        <div style={{ color: "#333", fontSize: 11, fontFamily: "monospace", textAlign: "center", padding: 8 }}>
            No threads detected
        </div>
    );
    const colors = ["#00ff88","#00aaff","#ff8800","#ff00aa","#aa00ff"];
    return (
        <div>
            <div style={{ color: "#888", fontSize: 10, fontFamily: "monospace", marginBottom: 6 }}>
                Scheduler: <span style={{ color: "#00ff88" }}>{scheduleMode}</span>
            </div>
            {threads.map((t, i) => (
                <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 8, marginBottom: 4,
                    background: "#0a0a1a", borderRadius: 4, padding: "4px 8px",
                    border: `1px solid ${colors[i%5]}33`,
                }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%",
                        background: colors[i%5], boxShadow: `0 0 6px ${colors[i%5]}` }}/>
                    <div style={{ flex: 1, color: colors[i%5], fontSize: 11, fontFamily: "monospace" }}>{t.name}</div>
                    <div style={{ color: "#666", fontSize: 10, fontFamily: "monospace" }}>P:{t.prio}</div>
                    <div style={{
                        width: `${Math.min(100, t.prio*20)}%`, maxWidth: 60, height: 3,
                        background: colors[i%5], borderRadius: 2,
                    }}/>
                </div>
            ))}
        </div>
    );
}

// ─── Interrupt Monitor ────────────────────────────────────────────────────────
function InterruptPanel({ interrupts, manualIRQ }) {
    return (
        <div>
            {interrupts.length === 0 && !manualIRQ && (
                <div style={{ color: "#333", fontSize: 11, fontFamily: "monospace", textAlign: "center" }}>
                    No interrupts attached
                </div>
            )}
            {interrupts.map((irq, i) => (
                <div key={i} style={{
                    display: "flex", gap: 8, alignItems: "center", marginBottom: 4,
                    background: "#1a0a0a", borderRadius: 4, padding: "4px 8px",
                    border: "1px solid #ff444433",
                }}>
                    <div style={{ color: "#ff4444", fontSize: 10 }}>⚡</div>
                    <div style={{ color: "#ff8888", fontSize: 11, fontFamily: "monospace", flex: 1 }}>
                        {irq.pin} → {irq.handler}()
                    </div>
                    <div style={{ width: 6, height: 6, borderRadius: "50%",
                        background: manualIRQ ? "#ff4444" : "#440000",
                        animation: manualIRQ ? "blink 0.3s infinite" : "none",
                    }}/>
                </div>
            ))}
            {manualIRQ && (
                <div style={{ color: "#ff4444", fontSize: 10, fontFamily: "monospace",
                    textAlign: "center", marginTop: 4 }}>
                    ⚡ INTERRUPT FIRED
                </div>
            )}
        </div>
    );
}

// ─── Code Editor ──────────────────────────────────────────────────────────────
const EXAMPLE_CODES = {
    "Motors + LCD": `// Motor control with LCD feedback
void setup() {
  lcdPrint("Motor Control v1");
  setMotorSpeed(1, 45);
  setMotorSpeed(2, 70);
  setMotorSpeed(3, 30);
  delay_ms(500);
}

void loop() {
  lcdPrint("M1:45% M2:70%");
  setLED(1, ON);
  setLED(4, ON);
}`,

    "Alarm System": `// Alarm triggered by interrupt
void ISR_buttonPress() {
  setBuzzer(ON, 1000);
  setLED(1, ON);
  setLED(2, ON);
  lcdPrint("!! ALARM !!");
}

void setup() {
  attachInterrupt(INT0, ISR_buttonPress);
  lcdPrint("Alarm Ready");
  setBuzzer(OFF);
}

void loop() {
  delay_ms(100);
}`,

    "Multithreading": `// RTOS multithreading with priority
RTOS_SCHEDULE(PRIORITY);

createThread("MotorCtrl", 3);
createThread("SensorRead", 2);
createThread("Display", 1);

void MotorCtrl() {
  setMotorSpeed(1, 80);
  setMotorSpeed(2, 60);
  setMotorSpeed(3, 40);
  setPriority(1, 3);
}

void SensorRead() {
  lcdPrint("Accel: 9.8m/s2");
  attachInterrupt(INT1, emergencyStop);
}

void Display() {
  setLED(1, ON);
  setLED(3, ON);
  setLED(5, ON);
}`,

    "PWM Ramp": `// PWM acceleration ramp
void setup() {
  lcdPrint("PWM Ramp Test");
  setBuzzer(ON, 440);
  delay_ms(200);
  setBuzzer(OFF);
}

void loop() {
  // Accelerate motor 1 gradually
  for(int pwm = 0; pwm <= 90; pwm += 10) {
    setMotorSpeed(1, pwm);
    lcdPrint("Ramping...");
    delay_ms(100);
  }
  setLED(2, ON);
  setMotorSpeed(2, 55);
  setMotorSpeed(3, 75);
}`,

    "Full System": `// Full embedded system demo
RTOS_SCHEDULE(RR);

createThread("Drive", 3);
createThread("Safety", 5);
createThread("HMI", 1);

void Drive() {
  setMotorSpeed(1, 65);
  setMotorSpeed(2, 65);
  setMotorSpeed(3, 50);
  accelerate(MOTOR_1, 0, 80, 2000);
}

void Safety() {
  attachInterrupt(INT0, emergencyStop);
  attachInterrupt(INT1, slowDown);
  setPriority(1, 5);
  setPriority(2, 3);
}

void HMI() {
  lcdPrint("SYS OK 3 Motors");
  setLED(1, ON);
  setLED(2, ON);
  setLED(3, ON);
  setLED(4, ON);
  setBuzzer(OFF);
}

void emergencyStop() {
  stopAll();
  setBuzzer(ON, 1500);
  lcdPrint("E-STOP TRIGGERED");
}`,
};

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function EmbeddedSimPlatform() {
    const [code, setCode] = useState(EXAMPLE_CODES["Full System"]);
    const [simState, setSimState] = useState(null);
    const [running, setRunning] = useState(false);
    const [manualIRQ, setManualIRQ] = useState(false);
    const [manualBuzzer, setManualBuzzer] = useState(false);
    const [log, setLog] = useState([]);
    const [activeTab, setActiveTab] = useState("simulator");
    const [selectedExample, setSelectedExample] = useState("Full System");

    const addLog = useCallback((msg, type="info") => {
        const ts = new Date().toISOString().slice(11,19);
        setLog(l => [{ts, msg, type}, ...l].slice(0, 50));
    }, []);

    const runCode = useCallback(() => {
        setRunning(true);
        addLog("Compiling embedded C...", "info");
        setTimeout(() => {
            try {
                const result = parseAndRunEmbeddedC(code, {});
                setSimState(result);
                setRunning(false);
                addLog(`✓ Compiled: ${result.linesExecuted} lines`, "success");
                if (result.motorSpeeds.some(s=>s>0)) addLog(`Motors: [${result.motorSpeeds.join("%, ")}%]`, "success");
                if (result.buzzerOn) addLog(`Buzzer active @ ${result.buzzerFreq}Hz`, "warn");
                if (result.threads.length) addLog(`${result.threads.length} thread(s) created [${result.scheduleMode}]`, "info");
                if (result.interrupts.length) addLog(`${result.interrupts.length} interrupt(s) attached`, "info");
                if (result.lcdText) addLog(`LCD: "${result.lcdText}"`, "info");
                if (result.totalDelay) addLog(`Total delay: ${result.totalDelay}ms`, "info");
            } catch(e) {
                addLog(`✗ Error: ${e.message}`, "error");
                setRunning(false);
            }
        }, 600);
    }, [code, addLog]);

    const triggerIRQ = useCallback(() => {
        setManualIRQ(true);
        setManualBuzzer(true);
        addLog("⚡ Interrupt button pressed!", "warn");
        if (simState) {
            setSimState(s => s ? {
                ...s,
                buzzerOn: true,
                lcdText: "!! IRQ FIRED !!",
                motorSpeeds: s.motorSpeeds.map(sp => Math.max(0, sp - 20)),
            } : s);
        }
        setTimeout(() => { setManualIRQ(false); setManualBuzzer(false); }, 1500);
    }, [simState, addLog]);

    const reset = () => {
        setSimState(null);
        setManualIRQ(false);
        setManualBuzzer(false);
        addLog("System reset", "info");
    };

    const state = simState ? {
        ...simState,
        buzzerOn: simState.buzzerOn || manualBuzzer,
    } : null;

    const logColors = { info:"#888", success:"#00ff88", error:"#ff4444", warn:"#ffaa00" };

    return (
        <div style={{
            minHeight: "100vh",
            background: "#050510",
            color: "#e0e0ff",
            fontFamily: "'Segoe UI', sans-serif",
            display: "flex", flexDirection: "column",
        }}>
            <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }
        @keyframes pulse { 0%,100%{opacity:0.6} 50%{opacity:1} }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #0a0a1a; }
        ::-webkit-scrollbar-thumb { background: #333; border-radius: 3px; }
        textarea { resize: none; }
        textarea::placeholder { color: #333; }
      `}</style>

            {/* Header */}
            <div style={{
                background: "linear-gradient(90deg, #0a0a20, #0f0f2e, #0a0a20)",
                borderBottom: "1px solid #1a1a3a",
                padding: "12px 24px",
                marginTop:"1%",
                display: "flex", alignItems: "center", gap: 16,
            }}>
                <div style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: "linear-gradient(135deg, #00ff88, #00aaff)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 18, fontWeight: "bold", color: "#000",
                }}>⚙</div>
                <div>
                    <div style={{
                        fontSize: 18, fontWeight: 700, letterSpacing: 1,
                        background: "linear-gradient(90deg, #00ff88, #00aaff)",
                        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"
                    }}>
                        EmbeddedSim Pro
                    </div>
                    <div style={{color: "#666", fontSize: 11}}>Interactive Embedded C & PLC Teaching Platform</div>
                    <div style={{color: "#666", fontSize: 11}}>Curriculum and Platform Developer Engr. Hazzan Ajao </div>
                </div>
                <div style={{marginLeft: "auto", display: "flex", gap: 8}}>
                {["simulator","reference","about"].map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)} style={{
                            background: activeTab===tab ? "#1a1a3a" : "transparent",
                            border: `1px solid ${activeTab===tab ? "#3a3a6a" : "#1a1a2a"}`,
                            color: activeTab===tab ? "#00ff88" : "#666",
                            padding: "6px 14px", borderRadius: 6, cursor: "pointer",
                            fontSize: 12, textTransform: "capitalize", transition: "all 0.2s",
                        }}>
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {activeTab === "simulator" && (
                <div style={{ flex: 1, display: "flex", gap: 0, overflow: "hidden" }}>

                    {/* LEFT: Code Editor */}
                    <div style={{
                        width: 380, minWidth: 320, borderRight: "1px solid #1a1a3a",
                        display: "flex", flexDirection: "column", background: "#07071a",
                    }}>
                        {/* Example selector */}
                        <div style={{ padding: "10px 12px", borderBottom: "1px solid #1a1a2a",
                            display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {Object.keys(EXAMPLE_CODES).map(k => (
                                <button key={k} onClick={() => { setCode(EXAMPLE_CODES[k]); setSelectedExample(k); }} style={{
                                    background: selectedExample===k ? "#1a3a1a" : "transparent",
                                    border: `1px solid ${selectedExample===k ? "#00ff8844" : "#1a1a2a"}`,
                                    color: selectedExample===k ? "#00ff88" : "#555",
                                    padding: "3px 8px", borderRadius: 4, cursor: "pointer", fontSize: 10,
                                }}>
                                    {k}
                                </button>
                            ))}
                        </div>

                        {/* Editor header */}
                        <div style={{ padding: "8px 12px", borderBottom: "1px solid #1a1a2a",
                            display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ display: "flex", gap: 5 }}>
                                {["#ff5f57","#febc2e","#28c840"].map(c => (
                                    <div key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c }}/>
                                ))}
                            </div>
                            <span style={{ color: "#555", fontSize: 11, fontFamily: "monospace", flex:1 }}>main.c</span>
                            <span style={{ color: "#333", fontSize: 10 }}>Embedded C</span>
                        </div>

                        {/* Code textarea */}
                        <textarea
                            value={code}
                            onChange={e => setCode(e.target.value)}
                            placeholder="// Paste your embedded C code here..."
                            style={{
                                flex: 1, background: "transparent", border: "none", outline: "none",
                                color: "#c8d0ff", fontSize: 12, lineHeight: 1.7, padding: "12px",
                                fontFamily: "'Courier New', Courier, monospace",
                            }}
                        />

                        {/* Controls */}
                        <div style={{ padding: "10px 12px", borderTop: "1px solid #1a1a2a",
                            display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button onClick={runCode} disabled={running} style={{
                                flex: 1, background: running ? "#0a1a0a" : "linear-gradient(135deg, #00ff88, #00cc66)",
                                border: "none", color: running ? "#00ff8866" : "#000",
                                padding: "9px 0", borderRadius: 6, cursor: running?"wait":"pointer",
                                fontWeight: 700, fontSize: 13, transition: "all 0.2s",
                            }}>
                                {running ? "⏳ Running..." : "▶ Flash & Run"}
                            </button>
                            <button onClick={reset} style={{
                                background: "transparent", border: "1px solid #333",
                                color: "#666", padding: "9px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
                            }}>↺</button>
                            <button onClick={triggerIRQ} style={{
                                background: "linear-gradient(135deg, #ff4444, #cc0000)",
                                border: "none", color: "#fff",
                                padding: "9px 12px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700,
                            }}>⚡ IRQ</button>
                        </div>

                        {/* Console log */}
                        <div style={{
                            height: 160, borderTop: "1px solid #1a1a2a",
                            background: "#030308", overflow: "auto", padding: "8px",
                        }}>
                            <div style={{ color: "#333", fontSize: 10, fontFamily: "monospace", marginBottom: 4 }}>
                                SERIAL MONITOR
                            </div>
                            {log.map((l, i) => (
                                <div key={i} style={{
                                    fontFamily: "monospace", fontSize: 10, color: logColors[l.type] || "#888",
                                    marginBottom: 1,
                                }}>
                                    <span style={{ color: "#333" }}>[{l.ts}] </span>{l.msg}
                                </div>
                            ))}
                            {log.length === 0 && (
                                <div style={{ color: "#222", fontFamily: "monospace", fontSize: 10 }}>
                                    Ready. Flash code to begin.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: Hardware Visualization */}
                    <div style={{ flex: 1, overflow: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>

                        {/* Top row: LCD + Buzzer + LEDs */}
                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                            {/* LCD */}
                            <div style={{ flex: 1, minWidth: 220 }}>
                                <div style={{ color: "#555", fontSize: 10, fontFamily: "monospace", marginBottom: 6 }}>
                                    ▸ LIQUID CRYSTAL DISPLAY
                                </div>
                                <LCDDisplay
                                    text={state?.lcdText}
                                    accelData={state?.accel?.some(Boolean) ? "9.81 m/s²" : null}
                                />
                            </div>

                            {/* Buzzer */}
                            <div style={{ minWidth: 140 }}>
                                <div style={{ color: "#555", fontSize: 10, fontFamily: "monospace", marginBottom: 6 }}>
                                    ▸ BUZZER / ALARM
                                </div>
                                <BuzzerAlarm active={!!state?.buzzerOn} freq={state?.buzzerFreq || 880} />
                            </div>

                            {/* LEDs */}
                            <div style={{ minWidth: 180 }}>
                                <div style={{ color: "#555", fontSize: 10, fontFamily: "monospace", marginBottom: 6 }}>
                                    ▸ STATUS LEDs
                                </div>
                                <div style={{
                                    background: "#0a0a1a", border: "1px solid #1a1a3a",
                                    borderRadius: 8, padding: 16,
                                }}>
                                    <LEDPanel leds={state?.leds || [false,false,false,false,false,false]} />
                                    <div style={{ color: "#333", fontSize: 9, fontFamily: "monospace", textAlign: "center", marginTop: 8 }}>
                                        GPIO 2–7
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Motors row */}
                        <div>
                            <div style={{ color: "#555", fontSize: 10, fontFamily: "monospace", marginBottom: 8 }}>
                                ▸ THREE-PHASE ELECTRIC MOTORS
                            </div>
                            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                                {[0,1,2].map(i => (
                                    <Motor3D
                                        key={i}
                                        index={i}
                                        speed={state?.motorSpeeds[i] ?? 0}
                                        priority={state?.priority[i] ?? 1}
                                        accel={state?.accel?.[i] ?? false}
                                        interruptActive={manualIRQ}
                                    />
                                ))}

                                {/* Speed control panel */}
                                <div style={{
                                    flex: 1, minWidth: 200,
                                    background: "#0a0a1a", border: "1px solid #1a1a3a", borderRadius: 12, padding: 16,
                                }}>
                                    <div style={{ color: "#555", fontSize: 10, fontFamily: "monospace", marginBottom: 10 }}>
                                        MANUAL SPEED OVERRIDE
                                    </div>
                                    {[0,1,2].map(i => (
                                        <div key={i} style={{ marginBottom: 12 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                                <span style={{ color: "#666", fontSize: 11, fontFamily: "monospace" }}>M{i+1}</span>
                                                <span style={{ color: "#00ff88", fontSize: 11, fontFamily: "monospace" }}>
                          {state?.motorSpeeds[i] ?? 0}%
                        </span>
                                            </div>
                                            <input type="range" min={0} max={100}
                                                   value={state?.motorSpeeds[i] ?? 0}
                                                   onChange={e => setSimState(s => s ? {
                                                       ...s, motorSpeeds: s.motorSpeeds.map((v,j) => j===i ? parseInt(e.target.value) : v)
                                                   } : { motorSpeeds: [0,0,0].map((v,j)=>j===i?parseInt(e.target.value):v), leds:[false,false,false,false,false,false], buzzerOn:false, lcdText:"Manual", threads:[], interrupts:[], priority:[1,1,1], accel:[false,false,false], scheduleMode:"FIFO" })}
                                                   style={{ width: "100%", accentColor: "#00ff88" }}
                                            />
                                        </div>
                                    ))}
                                    <button onClick={triggerIRQ} style={{
                                        width: "100%", background: "linear-gradient(135deg, #3a0000, #660000)",
                                        border: "1px solid #ff000044", color: "#ff8888",
                                        padding: "8px 0", borderRadius: 6, cursor: "pointer",
                                        fontSize: 12, fontWeight: 700, marginTop: 4,
                                    }}>
                                        ⚡ INTERRUPT BUTTON
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Bottom row: Threads + Interrupts + System info */}
                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                            {/* Thread panel */}
                            <div style={{
                                flex: 1, minWidth: 200,
                                background: "#0a0a1a", border: "1px solid #1a1a3a", borderRadius: 8, padding: 14,
                            }}>
                                <div style={{ color: "#555", fontSize: 10, fontFamily: "monospace", marginBottom: 8 }}>
                                    ▸ RTOS THREADS
                                </div>
                                <ThreadPanel threads={state?.threads ?? []} scheduleMode={state?.scheduleMode ?? "FIFO"} />
                            </div>

                            {/* Interrupt panel */}
                            <div style={{
                                flex: 1, minWidth: 200,
                                background: "#0a0a1a", border: "1px solid #1a1a3a", borderRadius: 8, padding: 14,
                            }}>
                                <div style={{ color: "#555", fontSize: 10, fontFamily: "monospace", marginBottom: 8 }}>
                                    ▸ INTERRUPT VECTORS
                                </div>
                                <InterruptPanel interrupts={state?.interrupts ?? []} manualIRQ={manualIRQ} />
                            </div>

                            {/* System metrics */}
                            <div style={{
                                flex: 1, minWidth: 180,
                                background: "#0a0a1a", border: "1px solid #1a1a3a", borderRadius: 8, padding: 14,
                            }}>
                                <div style={{ color: "#555", fontSize: 10, fontFamily: "monospace", marginBottom: 8 }}>
                                    ▸ SYSTEM METRICS
                                </div>
                                {[
                                    ["CPU Clock", "16 MHz"],
                                    ["Architecture", "AVR / ARM"],
                                    ["Flash", `${code.length} bytes`],
                                    ["Scheduler", state?.scheduleMode ?? "—"],
                                    ["Delay Total", state?.totalDelay ? `${state.totalDelay}ms` : "—"],
                                    ["Lines", state?.linesExecuted ?? "—"],
                                ].map(([k,v]) => (
                                    <div key={k} style={{ display:"flex", justifyContent:"space-between",
                                        marginBottom: 5, fontFamily: "monospace", fontSize: 11 }}>
                                        <span style={{ color: "#555" }}>{k}</span>
                                        <span style={{ color: "#00aaff" }}>{v}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {activeTab === "reference" && (
                <div style={{ flex: 1, overflow: "auto", padding: 28, maxWidth: 800, margin: "0 auto" }}>
                    <h2 style={{ color: "#00ff88", fontFamily: "monospace", marginBottom: 20 }}>
                        📖 EmbeddedSim API Reference
                    </h2>
                    {[
                        { cat: "Motors", fns: [
                                ["setMotorSpeed(n, speed)", "Set motor n (1–3) speed 0–100%"],
                                ["stopMotor(n)", "Stop motor n"],
                                ["stopAll()", "Stop all motors"],
                                ["setPriority(n, prio)", "Set motor n scheduling priority"],
                            ]},
                        { cat: "GPIO / LEDs", fns: [
                                ["setLED(n, ON|OFF)", "Control LED n (1–6)"],
                            ]},
                        { cat: "LCD Display", fns: [
                                ['lcdPrint("text")', "Print string to LCD (16×2)"],
                                ["lcdPrint(var)", "Print variable value"],
                            ]},
                        { cat: "Buzzer / Alarm", fns: [
                                ["setBuzzer(ON|OFF, freq?)", "Activate buzzer, optional frequency (Hz)"],
                                ["triggerAlarm()", "Fire alarm buzzer"],
                            ]},
                        { cat: "Timing", fns: [
                                ["delay_ms(ms)", "Block for ms milliseconds"],
                            ]},
                        { cat: "RTOS / Threads", fns: [
                                ['RTOS_SCHEDULE(PRIORITY|RR|FIFO)', "Set scheduling algorithm"],
                                ['createThread("name", priority)', "Create a named thread with priority"],
                            ]},
                        { cat: "Interrupts", fns: [
                                ["attachInterrupt(pin, handler)", "Attach ISR handler to pin"],
                            ]},
                    ].map(({ cat, fns }) => (
                        <div key={cat} style={{ marginBottom: 20 }}>
                            <div style={{ color: "#00aaff", fontSize: 12, fontFamily: "monospace",
                                marginBottom: 8, borderBottom: "1px solid #1a1a3a", paddingBottom: 4 }}>
                                {cat}
                            </div>
                            {fns.map(([fn, desc]) => (
                                <div key={fn} style={{ display: "flex", gap: 16, marginBottom: 6 }}>
                                    <code style={{ color: "#00ff88", fontSize: 12, minWidth: 280,
                                        background: "#0a0a1a", padding: "3px 8px", borderRadius: 4 }}>{fn}</code>
                                    <span style={{ color: "#888", fontSize: 12, lineHeight: 1.6 }}>{desc}</span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {activeTab === "about" && (
                <div style={{ flex: 1, overflow: "auto", padding: 28, maxWidth: 700, margin: "0 auto" }}>
                    <h2 style={{ color: "#00ff88", fontFamily: "monospace" }}>About EmbeddedSim Pro</h2>
                    <p style={{ color: "#888", lineHeight: 1.8, fontSize: 14 }}>
                        EmbeddedSim Pro is a browser-based interactive teaching platform for Embedded C, Embedded Firmware,
                        RTOS concepts, and PLC programming. Write real-like embedded C code and watch the virtual hardware respond in real time.
                    </p>
                    <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        {[
                            { icon: "⚙", title: "3D Electric Motors", desc: "3 motors with speed control, priority scheduling, and interrupt response" },
                            { icon: "📟", title: "LCD Display", desc: "16×2 LCD showing sensor data, system messages, and acceleration readings" },
                            { icon: "🔔", title: "Buzzer / Alarm", desc: "Audio alarm with configurable frequency, triggered by code or interrupt" },
                            { icon: "💡", title: "LED Status Panel", desc: "6 GPIO LEDs for visual I/O state representation" },
                            { icon: "⚡", title: "Interrupt System", desc: "ISR vectors, physical interrupt button, priority-driven preemption" },
                            { icon: "🧵", title: "RTOS Threads", desc: "Thread creation, synchronization, FIFO / Round Robin / Priority scheduling" },
                        ].map(({ icon, title, desc }) => (
                            <div key={title} style={{
                                background: "#0a0a1a", border: "1px solid #1a1a3a",
                                borderRadius: 8, padding: 14,
                            }}>
                                <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
                                <div style={{ color: "#00aaff", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{title}</div>
                                <div style={{ color: "#666", fontSize: 12, lineHeight: 1.6 }}>{desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
